import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { demoDataProvider } from '@/demo/demoProvider';
import type { DemoDomain } from '@/demo/demoFixtures';
import { useDemoMode } from '@/demo/DemoContext';
import { logDemoAuditEvent } from '@/demo/demoAudit';

export default function DemoDomainEvidence() {
  const { domain } = useOutletContext<{ domain: DemoDomain }>();
  const { enforcementState, policyTier } = useDemoMode();

  const payload = useMemo(() => {
    return demoDataProvider.exportEvidence(domain.site_id, enforcementState, policyTier);
  }, [domain.site_id, enforcementState, policyTier]);

  const handleDownload = async () => {
    const exportPayload = await payload;
    if (!exportPayload) return;
    logDemoAuditEvent(
      'demo_evidence_exported',
      { site_id: domain.site_id, domain: domain.domain },
      'Demo evidence export generated',
    );
    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `demo-evidence-${domain.domain}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="border-border/60">
      <CardHeader>
        <CardTitle>Demo Evidence Export (Simulated)</CardTitle>
        <CardDescription>
          Generates a demo-only evidence snapshot. The export is labeled and never touches real customer data.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge variant="outline">DEMO</Badge>
          <span className="text-sm text-muted-foreground">Demo Mode - simulated export.</span>
        </div>
        <div className="text-sm text-muted-foreground">Policy tier: {policyTier}</div>
        <div className="text-sm text-muted-foreground">Enforcement toggles: {Object.keys(enforcementState).length}</div>
        <Button onClick={handleDownload}>Download demo evidence JSON</Button>
      </CardContent>
    </Card>
  );
}
